import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { YStack } from "tamagui";

import AuthScaffold, { AuthFooterText, AuthInfoPanel } from "@/components/AuthScaffold";
import { supabase } from "@/integrations/supabase/client";
import {
  AppButton,
  AppFieldGroup,
  AppInput,
  AppText,
  AppTextArea,
} from "../../ui-foundation";
import {
  AUTH_SUPPORT_SUCCESS_MESSAGE,
  buildAuthSupportMailto,
  KINDRED_SUPPORT_EMAIL,
  validateAuthSupportRequest,
} from "../../shared/support";

const LoginSupport = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [issue, setIssue] = useState("");
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<{ email?: string; issue?: string }>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const requestedEmail = searchParams.get("email")?.trim();
    if (requestedEmail) {
      setEmail(requestedEmail);
    }
  }, [searchParams]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateAuthSupportRequest({ email, issue });
    setErrors(validation.errors);
    setSubmitError("");

    if (!validation.isValid) {
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.functions.invoke("submit-auth-support-request", {
      body: {
        name: name.trim().slice(0, 100),
        email: validation.email,
        issue: validation.issue,
        website,
        source: "web",
      },
    });
    setLoading(false);

    if (error || !data?.ok) {
      setSubmitError(
        data?.error ||
          "We could not send that request just now. Please use the email support option below.",
      );
      return;
    }

    setSent(true);
  };

  const openEmailSupport = () => {
    window.location.href = buildAuthSupportMailto(email);
  };

  return (
    <AuthScaffold
      eyebrow="Login support"
      title="Having trouble signing in?"
      description="Tell us what is happening and the Kindred admin team will help you get back in."
      highlights={[]}
      showContext={false}
    >
      {sent ? (
        <AuthInfoPanel>
          <YStack alignItems="center" gap="$sm">
            <AppText textAlign="center">{AUTH_SUPPORT_SUCCESS_MESSAGE}</AppText>
            <Link to="/signin" className="kindred-inline-link">
              Back to sign in
            </Link>
          </YStack>
        </AuthInfoPanel>
      ) : (
        <YStack tag="form" onSubmit={handleSubmit} gap="$sm">
          <AppFieldGroup label={<label htmlFor="support-name">Name (optional)</label>}>
            <AppInput
              id="support-name"
              value={name}
              onChangeText={(value) => setName(value.slice(0, 100))}
              placeholder="Your name"
              autoComplete="name"
            />
          </AppFieldGroup>

          <AppFieldGroup
            label={<label htmlFor="support-email">Kindred email</label>}
            error={errors.email}
          >
            <AppInput
              id="support-email"
              type="email"
              value={email}
              onChangeText={setEmail}
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect={false}
              spellCheck={false}
              inputMode="email"
            />
          </AppFieldGroup>

          <AppFieldGroup
            label={<label htmlFor="support-issue">What happens when you try?</label>}
            error={errors.issue}
          >
            <AppTextArea
              id="support-issue"
              value={issue}
              onChangeText={(value) => setIssue(value.slice(0, 2000))}
              placeholder="For example: I requested a password reset but the email has not arrived."
              minHeight={132}
              autoCapitalize="sentences"
            />
          </AppFieldGroup>

          <input
            aria-hidden="true"
            autoComplete="off"
            name="website"
            tabIndex={-1}
            hidden
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />

          {submitError ? (
            <AuthInfoPanel>
              <AppText color="$danger" textAlign="center" fontSize="$sm" lineHeight="$md">
                {submitError}
              </AppText>
            </AuthInfoPanel>
          ) : null}

          <AppButton type="submit" disabled={loading} width="100%">
            {loading ? "Sending..." : "Request support"}
          </AppButton>
          <AppButton type="button" tone="outline" onPress={openEmailSupport} width="100%">
            Email support instead
          </AppButton>

          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
            Replies will come to the email address you provide.
          </AppText>
        </YStack>
      )}

      <AuthFooterText>
        <Link to="/signin" className="kindred-inline-link">
          Back to sign in
        </Link>
        {" · "}
        <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
          {KINDRED_SUPPORT_EMAIL}
        </a>
      </AuthFooterText>
    </AuthScaffold>
  );
};

export default LoginSupport;
